'use client'

import { useState, type ReactNode } from 'react'

import { Checkbox } from '@/components/ui/checkbox'
import { listMenuItemLabelClass, listMenuItemVariants } from '@/lib/component-constants'

export interface ListMenuItemProps {
  label: string
  showCheckbox?: boolean
  prependIcon?: ReactNode
  isSelected?: boolean
  disabled?: boolean
  onClick?: (selected: boolean) => void
}

export function ListMenuItem({
  label,
  showCheckbox = false,
  prependIcon,
  isSelected = false,
  disabled = false,
  onClick,
}: ListMenuItemProps) {
  const [pressedKey, setPressedKey] = useState<string | null>(null)

  function activate() {
    if (disabled) return
    onClick?.(!isSelected)
  }

  return (
    <div
      role="button"
      aria-label={label}
      aria-pressed={isSelected}
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? -1 : 0}
      data-slot="list-menu-item"
      data-selected={isSelected ? '' : undefined}
      data-disabled={disabled ? '' : undefined}
      data-keyboard-pressed={pressedKey !== null ? '' : undefined}
      className={listMenuItemVariants()}
      onClick={activate}
      onKeyDown={(event) => {
        if (disabled) return
        if (event.key !== 'Enter' && event.key !== ' ') return
        event.preventDefault()
        if (event.repeat) return
        setPressedKey(event.key)
        if (event.key === 'Enter') activate()
      }}
      onKeyUp={(event) => {
        if (disabled) return
        if (event.key !== 'Enter' && event.key !== ' ') return
        event.preventDefault()
        if (event.repeat) return
        if (event.key === ' ' && pressedKey === ' ') activate()
        setPressedKey(null)
      }}
      onBlur={() => setPressedKey(null)}
    >
      {showCheckbox && (
        <span inert aria-hidden="true" className="pointer-events-none flex size-5 shrink-0 items-center justify-center">
          <Checkbox checked={isSelected} readOnly tabIndex={-1} size="md" />
        </span>
      )}
      <div className="flex">
        {prependIcon != null && (
          <span
            inert
            aria-hidden="true"
            className="pointer-events-none flex size-6 shrink-0 items-center justify-center"
          >
            {prependIcon}
          </span>
        )}
        <span className={listMenuItemLabelClass}>{label}</span>
      </div>
    </div>
  )
}
